import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

interface DownloadLink {
  formName: string;
  fileName: string;
  downloadUrl: string;
  pageCount: number;
}

interface RetrievalResponse {
  success: boolean;
  submissionId?: string;
  memberName?: string;
  submittedAt?: string;
  downloadLinks?: DownloadLink[];
  message?: string;
  error?: string;
}

export const RetrievePage: React.FC = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://54.144.102.147:3001/api'  
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') || '';
  
  const [token, setToken] = useState(tokenFromUrl);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloadLinks, setDownloadLinks] = useState<DownloadLink[]>([]);
  const [submissionInfo, setSubmissionInfo] = useState<{
    submissionId: string;
    memberName: string;
    submittedAt: string;
  } | null>(null);

  // Auto-focus email field if token is present
  useEffect(() => {
    if (tokenFromUrl) {
      document.getElementById('email-input')?.focus();
    }
  }, [tokenFromUrl]);

  const handleVerify = async () => {
    if (!token.trim() || !email.trim()) {
      setError('Please enter both token and email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/retrieval/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email }),
      });

      const data: RetrievalResponse = await response.json();

      if (data.success && data.downloadLinks) {
        setDownloadLinks(data.downloadLinks);
        setSubmissionInfo({
          submissionId: data.submissionId!,
          memberName: data.memberName!,
          submittedAt: data.submittedAt!,
        });
      } else {
        setError(data.error || 'Verification failed. Please check your token and email.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleVerify();
    }
  };

  const formatToken = (value: string): string => {
    // Remove non-alphanumeric characters
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    
    // Add hyphens every 4 characters
    const parts: string[] = [];
    for (let i = 0; i < cleaned.length; i += 4) {
      parts.push(cleaned.substring(i, i + 4));
    }
    
    return parts.join('-').substring(0, 14); // XXXX-YYYY-ZZZZ
  };

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToken(formatToken(e.target.value));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-blue-900 mb-2">Retrieve Your Forms</h1>
          <p className="text-gray-600">
            Secure HIPAA-compliant document retrieval
          </p>
        </div>

        {!downloadLinks.length ? (
          // Verification Form
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                Enter Your Credentials
              </h2>
              <p className="text-gray-600 text-sm">
                Please enter the retrieval token from your email and your email address to access your forms.
              </p>
            </div>

            <div className="space-y-5">
              {/* Token Input */}
              <div>
                <label htmlFor="token-input" className="block text-sm font-medium text-gray-700 mb-2">
                  Retrieval Token <span className="text-red-500">*</span>
                </label>
                <input
                  id="token-input"
                  type="text"
                  value={token}
                  onChange={handleTokenChange}
                  onKeyPress={handleKeyPress}
                  placeholder="XXXX-YYYY-ZZZZ"
                  maxLength={14}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-lg tracking-wider"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Format: XXXX-YYYY-ZZZZ (12 characters)
                </p>
              </div>

              {/* Email Input */}
              <div>
                <label htmlFor="email-input" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must match the email provided during form submission
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <span className="text-red-500 mr-2">Warning:</span>
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleVerify}
                disabled={loading || !token || !email}
                className={`w-full px-6 py-4 rounded-lg font-semibold text-white transition-all ${
                  loading || !token || !email
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Verifying...
                  </span>
                ) : (
                  'Retrieve Forms'
                )}
              </button>
            </div>

            {/* Security Notice */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
                <span className="mr-2">Secure</span>
                Security & Privacy
              </h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>- All downloads are encrypted and logged</li>
                <li>- PDF links do not expire</li>
                <li>- Documents contain Protected Health Information (PHI)</li>
              </ul>
            </div>

            {/* Help Section */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Lost your token?{' '}
                <a href="/retrieve/request" className="text-blue-600 hover:underline font-medium">
                  Request a new one
                </a>
              </p>
            </div>
          </div>
        ) : (
          // Download Links Display
          <div className="space-y-6">
            {/* Success Banner */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start">
                <span className="text-green-500 text-2xl mr-3">OK</span>
                <div>
                  <h2 className="text-xl font-semibold text-green-900 mb-1">
                    Verification Successful!
                  </h2>
                  <p className="text-green-700">
                    Your forms are ready for download.
                  </p>
                </div>
              </div>
            </div>

            {/* Submission Info */}
            {submissionInfo && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="font-semibold text-gray-800 mb-3">Submission Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Member Name:</span>
                    <span className="ml-2 font-medium">{submissionInfo.memberName}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Submitted:</span>
                    <span className="ml-2 font-medium">
                      {new Date(submissionInfo.submittedAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-gray-600">Submission ID:</span>
                    <span className="ml-2 font-mono text-xs">{submissionInfo.submissionId}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Download Links */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                Your Documents ({downloadLinks.length})
              </h3>
              
              <div className="space-y-3">
                {downloadLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 group-hover:text-blue-600">
                          {link.formName}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {link.fileName} - {link.pageCount} page{link.pageCount !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <div className="ml-4">
                        <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg group-hover:bg-blue-700">
                          Download
                          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Done Button */}
            <div className="text-center">
              <button
                onClick={() => {
                  setDownloadLinks([]);
                  setToken('');
                  setEmail('');
                  setSubmissionInfo(null);
                }}
                className="px-8 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RetrievePage;

